import { useState, useCallback } from 'react';
import * as ImagePicker from 'expo-image-picker';
import { Alert, Linking } from 'react-native';

export interface ImagePickerResult {
  uri: string;
  base64?: string;
  width: number;
  height: number;
  fileSize?: number;
  type?: string;
  fileName?: string;
  mimeType?: string;
}

// Alias for backwards compatibility
export type PickedImage = ImagePickerResult;

export interface ImagePickerOptions {
  allowsEditing?: boolean;
  aspect?: [number, number];
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  base64?: boolean;
  mediaTypes?: ImagePicker.MediaTypeOptions;
  maxFileSizeBytes?: number; // Default: 5MB
  allowedFormats?: string[]; // Default: ['jpg', 'jpeg', 'png', 'webp']
}

export interface ImagePickerError {
  code: 'PERMISSION_DENIED' | 'CANCELLED' | 'FILE_TOO_LARGE' | 'INVALID_FORMAT' | 'UNKNOWN';
  message: string;
}

// Default max file size: 5MB
const DEFAULT_MAX_FILE_SIZE = 5 * 1024 * 1024;

// Default allowed formats
const DEFAULT_ALLOWED_FORMATS = ['jpg', 'jpeg', 'png', 'webp'];

/**
 * Validates the selected image file
 */
function validateImage(
  asset: ImagePicker.ImagePickerAsset,
  options: ImagePickerOptions
): { isValid: boolean; error?: ImagePickerError } {
  const maxFileSize = options.maxFileSizeBytes ?? DEFAULT_MAX_FILE_SIZE;
  const allowedFormats = options.allowedFormats ?? DEFAULT_ALLOWED_FORMATS;

  // Validate file size
  if (asset.fileSize && asset.fileSize > maxFileSize) {
    const maxSizeMB = (maxFileSize / (1024 * 1024)).toFixed(1);
    const actualSizeMB = (asset.fileSize / (1024 * 1024)).toFixed(1);
    return {
      isValid: false,
      error: {
        code: 'FILE_TOO_LARGE',
        message: `Image file size (${actualSizeMB}MB) exceeds maximum allowed size of ${maxSizeMB}MB. Please select a smaller image.`,
      },
    };
  }

  // Validate file format
  if (asset.uri) {
    const extension = asset.uri.split('.').pop()?.toLowerCase();
    const mimeType = asset.mimeType?.toLowerCase();

    // Check extension or MIME type
    const isValidFormat =
      (extension && allowedFormats.includes(extension)) ||
      (mimeType && allowedFormats.some(format => mimeType.includes(format)));

    if (!isValidFormat) {
      return {
        isValid: false,
        error: {
          code: 'INVALID_FORMAT',
          message: `Invalid image format. Please select an image in one of these formats: ${allowedFormats.join(', ')}.`,
        },
      };
    }
  }

  return { isValid: true };
}

export function useImagePicker() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ImagePickerError | null>(null);

  const requestPermission = useCallback(
    async (type: 'camera' | 'mediaLibrary'): Promise<boolean> => {
      try {
        const permission = type === 'camera'
          ? await ImagePicker.requestCameraPermissionsAsync()
          : await ImagePicker.requestMediaLibraryPermissionsAsync();

        if (permission.status !== 'granted') {
          const permissionName = type === 'camera' ? 'camera' : 'photo library';
          const message = `Permission to access ${permissionName} is required to select a profile picture.`;

          if (permission.canAskAgain) {
            Alert.alert(
              'Permission Required',
              message,
              [
                { text: 'Cancel', style: 'cancel' },
                {
                  text: 'Grant Permission',
                  onPress: async () => {
                    await requestPermission(type);
                  },
                },
              ]
            );
          } else {
            Alert.alert(
              'Permission Denied',
              `${message} Please enable it in your device settings.`,
              [
                { text: 'Cancel', style: 'cancel' },
                {
                  text: 'Open Settings',
                  onPress: () => {
                    Linking.openSettings();
                  },
                },
              ]
            );
          }

          setError({
            code: 'PERMISSION_DENIED',
            message: `${permissionName} permission denied`,
          });
          return false;
        }

        return true;
      } catch (err) {
        setError({
          code: 'UNKNOWN',
          message: err instanceof Error ? err.message : 'Failed to request permission',
        });
        return false;
      }
    },
    []
  );

  const pickImage = useCallback(
    async (options: ImagePickerOptions = {}): Promise<ImagePickerResult | null> => {
      setIsLoading(true);
      setError(null);

      try {
        // Request permission first
        const hasPermission = await requestPermission('mediaLibrary');
        if (!hasPermission) {
          setIsLoading(false);
          return null;
        }

        // Launch image picker
        const result = await ImagePicker.launchImageLibraryAsync({
          mediaTypes: options.mediaTypes || ImagePicker.MediaTypeOptions.Images,
          allowsEditing: options.allowsEditing ?? true,
          aspect: options.aspect || [1, 1],
          quality: options.quality ?? 0.8,
          base64: options.base64 ?? false,
        });

        if (result.canceled) {
          setError({
            code: 'CANCELLED',
            message: 'Image selection cancelled',
          });
          setIsLoading(false);
          return null;
        }

        const asset = result.assets?.[0];

        if (!asset) {
          setError({
            code: 'UNKNOWN',
            message: 'No image returned from picker',
          });
          setIsLoading(false);
          return null;
        }

        // Validate the selected image
        const validation = validateImage(asset, options);
        if (!validation.isValid) {
          setError(validation.error!);
          setIsLoading(false);

          // Show user-friendly alert
          Alert.alert(
            'Invalid Image',
            validation.error!.message,
            [{ text: 'OK' }]
          );

          return null;
        }

        const imageResult: ImagePickerResult = {
          uri: asset.uri,
          width: asset.width,
          height: asset.height,
        };

        if (asset.fileSize !== undefined) {
          imageResult.fileSize = asset.fileSize;
        }
        if (asset.type != null) {
          imageResult.type = asset.type;
        }
        if (asset.fileName) {
          imageResult.fileName = asset.fileName;
        }
        if (asset.mimeType) {
          imageResult.mimeType = asset.mimeType;
        }
        if (options.base64 && asset.base64) {
          imageResult.base64 = asset.base64;
        }

        setIsLoading(false);
        return imageResult;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to pick image';
        setError({
          code: 'UNKNOWN',
          message: errorMessage,
        });
        setIsLoading(false);
        return null;
      }
    },
    [requestPermission]
  );

  const takePhoto = useCallback(
    async (options: ImagePickerOptions = {}): Promise<ImagePickerResult | null> => {
      setIsLoading(true);
      setError(null);

      try {
        // Request permission first
        const hasPermission = await requestPermission('camera');
        if (!hasPermission) {
          setIsLoading(false);
          return null;
        }

        // Launch camera
        const result = await ImagePicker.launchCameraAsync({
          allowsEditing: options.allowsEditing ?? true,
          aspect: options.aspect || [1, 1],
          quality: options.quality ?? 0.8,
          base64: options.base64 ?? false,
        });

        if (result.canceled) {
          setError({
            code: 'CANCELLED',
            message: 'Photo capture cancelled',
          });
          setIsLoading(false);
          return null;
        }

        const asset = result.assets?.[0];

        if (!asset) {
          setError({
            code: 'UNKNOWN',
            message: 'No image returned from camera',
          });
          setIsLoading(false);
          return null;
        }

        // Validate the captured photo
        const validation = validateImage(asset, options);
        if (!validation.isValid) {
          setError(validation.error!);
          setIsLoading(false);

          // Show user-friendly alert
          Alert.alert(
            'Invalid Image',
            validation.error!.message,
            [{ text: 'OK' }]
          );

          return null;
        }

        const imageResult: ImagePickerResult = {
          uri: asset.uri,
          width: asset.width,
          height: asset.height,
        };

        if (asset.fileSize !== undefined) {
          imageResult.fileSize = asset.fileSize;
        }
        if (asset.type != null) {
          imageResult.type = asset.type;
        }
        if (asset.fileName) {
          imageResult.fileName = asset.fileName;
        }
        if (asset.mimeType) {
          imageResult.mimeType = asset.mimeType;
        }
        if (options.base64 && asset.base64) {
          imageResult.base64 = asset.base64;
        }

        setIsLoading(false);
        return imageResult;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to take photo';
        setError({
          code: 'UNKNOWN',
          message: errorMessage,
        });
        setIsLoading(false);
        return null;
      }
    },
    [requestPermission]
  );

  const showImagePickerOptions = useCallback(() => {
    Alert.alert(
      'Select Photo',
      'Choose a photo from your library or take a new one',
      [
        {
          text: 'Take Photo',
          onPress: () => takePhoto(),
        },
        {
          text: 'Choose from Library',
          onPress: () => pickImage(),
        },
        {
          text: 'Cancel',
          style: 'cancel',
        },
      ],
      { cancelable: true }
    );
  }, [pickImage, takePhoto]);

  return {
    pickImage,
    takePhoto,
    showImagePickerOptions,
    isLoading,
    error,
  };
}
